import React, { useEffect, useState } from "react";
import axios from "axios";
import apiUrl from "../../Apiurl";
import ImageZoomModal from "../../interfaccia/ImageZoomModal";

function revokeImages(images) {
  images.forEach((image) => {
    if (image?.blobUrl) URL.revokeObjectURL(image.blobUrl);
  });
}

function LibreriaImmaginiProdotto({
  codiceVS,
  token,
  refreshKey,
  onStatusChange,
  updatedBy,
  brandCode,
  onDeliveryScheduled,
  fallback = null,
}) {
  const [state, setState] = useState({
    loading: Boolean(codiceVS && token),
    images: [],
    version: null,
    error: "",
  });
  const [editing, setEditing] = useState(false);
  const [draftImages, setDraftImages] = useState([]);
  const [saveState, setSaveState] = useState({ pending: false, error: "" });
  const [deliveryState, setDeliveryState] = useState({ pending: false, message: "", error: "" });
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let loadedImages = [];

    const load = async () => {
      if (!codiceVS || !token) {
        setState({ loading: false, images: [], version: null, error: "" });
        if (typeof onStatusChange === "function") onStatusChange(false);
        return;
      }
      setState((previous) => ({ ...previous, loading: true, error: "" }));
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(
          `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini-library`,
          { headers },
        );
        const library = response?.data?.library ?? null;
        const metadata = Array.isArray(library?.images) ? library.images : [];
        loadedImages = await Promise.all(
          metadata.map(async (image) => {
            const binary = await axios.get(`${apiUrl}${image.url}`, {
              headers,
              responseType: "blob",
            });
            return { ...image, blobUrl: URL.createObjectURL(binary.data) };
          }),
        );
        if (cancelled) {
          revokeImages(loadedImages);
          return;
        }
        setState({
          loading: false,
          images: loadedImages,
          version: library?.version ?? null,
          error: "",
        });
        setDraftImages(loadedImages);
        setEditing(false);
        setSaveState({ pending: false, error: "" });
        setDeliveryState({ pending: false, message: "", error: "" });
        if (typeof onStatusChange === "function") {
          onStatusChange(loadedImages.length > 0);
        }
      } catch (error) {
        if (cancelled) return;
        setState({
          loading: false,
          images: [],
          version: null,
          error:
            error?.response?.data?.error ||
            "Libreria immagini permanente non disponibile.",
        });
        if (typeof onStatusChange === "function") onStatusChange(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      revokeImages(loadedImages);
    };
  }, [codiceVS, token, refreshKey, onStatusChange]);

  const moveImage = (index, direction) => {
    setDraftImages((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const saveManifest = async () => {
    if (!updatedBy) {
      setSaveState({
        pending: false,
        error: "Username Domino non disponibile: impossibile registrare la modifica.",
      });
      return;
    }
    setSaveState({ pending: true, error: "" });
    try {
      const response = await axios.patch(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini-library`,
        {
          expectedVersion: state.version,
          assetIds: draftImages.map((image) => image.assetId),
          updatedBy,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const version = response?.data?.library?.version ?? state.version + 1;
      const images = draftImages.map((image, index) => ({
        ...image,
        position: index + 1,
      }));
      const keptAssetIds = new Set(images.map((image) => image.assetId));
      revokeImages(
        state.images.filter((image) => !keptAssetIds.has(image.assetId)),
      );
      setState((current) => ({ ...current, images, version }));
      setDraftImages(images);
      setEditing(false);
      setSaveState({ pending: false, error: "" });
      if (typeof onStatusChange === "function") onStatusChange(images.length > 0);
    } catch (error) {
      setSaveState({
        pending: false,
        error: error?.response?.data?.error || "Modifica immagini non salvata.",
      });
    }
  };

  const scheduleDelivery = async () => {
    if (!updatedBy || !brandCode) {
      setDeliveryState({
        pending: false,
        message: "",
        error: "Username Domino o brand code non disponibile.",
      });
      return;
    }
    setDeliveryState({ pending: true, message: "", error: "" });
    try {
      const response = await axios.post(
        `${apiUrl}/moda/articoli/${encodeURIComponent(codiceVS)}/immagini-library/deliver`,
        {
          brandCode,
          requestedBy: updatedBy,
          targetSites: ["VIG", "BOT"],
          copyToModa: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const jobId = response?.data?.jobId;
      setDeliveryState({
        pending: false,
        message: jobId ? `Reinvio avviato: job ${jobId}.` : "Reinvio avviato.",
        error: "",
      });
      if (typeof onDeliveryScheduled === "function") onDeliveryScheduled(jobId);
    } catch (error) {
      setDeliveryState({
        pending: false,
        message: "",
        error: error?.response?.data?.error || "Reinvio immagini non avviato.",
      });
    }
  };

  if (state.loading) {
    return <div className="pa3 bg-near-white br2 gray">Caricamento immagini…</div>;
  }
  if (!state.images.length) {
    return (
      <div>
        {fallback}
        {state.error && <p className="f7 gray mt2 mb0">{state.error}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className={editing ? "flex flex-column items-center" : "modificaProdottiImmaginiR2Grid"}>
        {(editing ? draftImages : state.images).map((image, index, current) => (
          <figure key={image.assetId} className="ma0 mb3 tc w-100">
            <button
              type="button"
              className="modificaProdottiImmagineZoomTrigger"
              onClick={() => setZoomImage(image)}
              aria-label={`Immagine R2 ${image.position} di ${codiceVS}: apri l'originale`}
            >
              <img
                src={image.blobUrl}
                alt={`Immagine R2 ${image.position} di ${codiceVS}`}
                className="modificaProdottiImmaginiThumb br2"
                title="Clicca per ingrandire"
                loading="lazy"
              />
            </button>
            <figcaption className="f7 gray mt1">
              Posizione {editing ? index + 1 : image.position}
            </figcaption>
            {editing && (
              <div className="mt2 flex justify-center">
                <button
                  type="button"
                  className="mr1 ph2 pv1"
                  onClick={() => moveImage(index, -1)}
                  disabled={index === 0 || saveState.pending}
                  aria-label={`Sposta immagine ${index + 1} prima`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="mr1 ph2 pv1"
                  onClick={() => moveImage(index, 1)}
                  disabled={index === current.length - 1 || saveState.pending}
                  aria-label={`Sposta immagine ${index + 1} dopo`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="ph2 pv1 dark-red"
                  onClick={() => setDraftImages((images) =>
                    images.filter((item) => item.assetId !== image.assetId))}
                  disabled={saveState.pending}
                >
                  Rimuovi
                </button>
              </div>
            )}
          </figure>
        ))}
      </div>
      <ImageZoomModal
        open={Boolean(zoomImage)}
        onClose={() => setZoomImage(null)}
        src={zoomImage?.blobUrl || ""}
        alt={zoomImage ? `Immagine R2 ${zoomImage.position} di ${codiceVS}` : "Immagine R2"}
      />
      <p className="f7 gray mt0 mb2">
        Archivio Domino R2{state.version ? ` · revisione ${state.version}` : ""}
      </p>
      {!editing ? (
        <button
          type="button"
          className="ph3 pv2"
          onClick={() => {
            setDraftImages(state.images);
            setEditing(true);
            setSaveState({ pending: false, error: "" });
          }}
        >
          Riordina o rimuovi
        </button>
      ) : (
        <div className="flex flex-wrap">
          <button
            type="button"
            className="mr2 ph3 pv2"
            onClick={saveManifest}
            disabled={saveState.pending}
          >
            {saveState.pending ? "Salvataggio…" : "Salva revisione"}
          </button>
          <button
            type="button"
            className="ph3 pv2"
            onClick={() => {
              setDraftImages(state.images);
              setEditing(false);
              setSaveState({ pending: false, error: "" });
            }}
            disabled={saveState.pending}
          >
            Annulla
          </button>
        </div>
      )}
      {saveState.error && <p className="dark-red f7 mt2">{saveState.error}</p>}
      {!editing && (
        <div className="mt3 pt3 bt bordoColAlt">
          <button
            type="button"
            className="ph3 pv2"
            onClick={scheduleDelivery}
            disabled={deliveryState.pending}
          >
            {deliveryState.pending ? "Avvio reinvio…" : "Reinvia questa revisione"}
          </button>
          {deliveryState.message && (
            <p className="dark-green f7 mt2 mb0">{deliveryState.message}</p>
          )}
          {deliveryState.error && (
            <p className="dark-red f7 mt2 mb0">{deliveryState.error}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default LibreriaImmaginiProdotto;
